// packages
import { useMemo } from "react";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";

// components
import { FormField } from "@/shared/components/FormField";
import { ComboboxWithLabel } from "@/shared/components/ui/ComboboxWithLabel";

// utils
import Strings from "@/shared/utils/Strings.constants";
import { BreedUtils, BreedsCatalog } from "@/shared/utils/BreedUtils";
import { TOP_BREED_PATTERNS } from "@/shared/utils/constants";
import { cn } from "@/shared/utils";

// types
import { Quote } from "../types/Quote.interface";

type FloatingVariant = "floating" | "floating-inner";

interface UserModalBreedFieldsProps {
    /** Breed catalog for the state resolved from the zip currently typed in the modal. */
    catalog: BreedsCatalog;
    floatingVariant?: FloatingVariant;
    /** Grid classes for the slot — applied only while a field is actually showing. */
    className?: string;
}

/**
 * Breed re-selection inside <UserModal /> (step-2 "edit your info").
 *
 * Changing the zip here can move the customer to a state that doesn't offer a breed they already
 * picked on step 1 — e.g. a pit bull selected under a FL zip, then the zip changed to CA. Saving
 * that combination strands the Plan step on an infinite spinner, so the breed field is revealed
 * right under the zip for the customer to fix before saving.
 *
 * Visibility is derived, never latched, and the stored breedID is never cleared: type the original
 * zip back in and the breed is offered again, so the field simply goes away with the selection
 * intact. A field the customer has already edited stays put so their choice doesn't vanish
 * mid-interaction.
 */
export function UserModalBreedFields({ catalog, floatingVariant = "floating", className }: UserModalBreedFieldsProps) {
    const { control } = useFormContext<Partial<Quote>>();
    // `useWatch`/`useFormState`, not `watch`/`formState` off the context: this is a child of the
    // component that owns useForm, and context `watch` subscribes THAT component, not this one —
    // so a breed selection would never recompute what's below. RHF also mutates `policies` in
    // place, leaving the array identity stable, so a memo keyed on it would freeze too.
    const policies = useWatch({ control, name: "policies" });
    const { dirtyFields } = useFormState({ control, name: "policies" });

    const unofferedIndexes = useMemo(() => new Set(BreedUtils.getUnofferedBreedIndexes(policies, catalog)), [policies, catalog]);

    const visibleIndexes = (policies ?? []).reduce<number[]>((indexes, _policy, index) => {
        const isEdited = !!dirtyFields.policies?.[index]?.breedID;
        if (unofferedIndexes.has(index) || isEdited) indexes.push(index);
        return indexes;
    }, []);

    if (visibleIndexes.length === 0) return null;

    return (
        <div className={cn("flex flex-col gap-6", className)}>
            {visibleIndexes.map(index => (
                <PetBreedField
                    key={policies?.[index]?.id ?? `policy-${index}`}
                    policyIndex={index}
                    catalog={catalog}
                    isUnoffered={unofferedIndexes.has(index)}
                    floatingVariant={floatingVariant}
                />
            ))}
        </div>
    );
}

interface PetBreedFieldProps {
    policyIndex: number;
    catalog: BreedsCatalog;
    /** True while this pet's stored breed is missing from the resolved state's catalog. */
    isUnoffered: boolean;
    floatingVariant: FloatingVariant;
}

function PetBreedField({ policyIndex, catalog, isUnoffered, floatingVariant }: PetBreedFieldProps) {
    const { control, clearErrors } = useFormContext<Partial<Quote>>();
    const pet = useWatch({ control, name: `policies.${policyIndex}` });

    const options = useMemo(() => BreedUtils.formatBreedOptions(catalog.breeds, pet?.species, TOP_BREED_PATTERNS), [catalog.breeds, pet?.species]);

    const petLabel = pet?.name?.trim() ? Strings.BREED_FOR(pet.name.trim()) : Strings.BREED;

    return (
        <div className="duration-300 animate-in fade-in slide-in-from-top-2">
            <Controller
                name={`policies.${policyIndex}.breedID`}
                control={control}
                render={({ field: { value, onChange, ref } }) => {
                    // Purely derived: it shows the moment the field appears rather than waiting for
                    // a submit attempt, and stops the moment the breed is offered again. Deliberately
                    // NOT falling back to `fieldState.error` — the resolver reports this same message
                    // on a blocked Save, and that copy outlives the fix that resolved it.
                    const error = isUnoffered ? Strings.ERRORS.BREED_UNAVAILABLE_IN_STATE : undefined;

                    return (
                        <FormField className="flex-1" error={error} errorId={`error-modal-pet-breed-${policyIndex}`}>
                            <ComboboxWithLabel
                                ref={ref}
                                id={`modal.policies.${policyIndex}.breed`}
                                labelId={`modal.policies.${policyIndex}.breed-label`}
                                label={petLabel}
                                // An unoffered id matches no option, so the combobox shows the
                                // placeholder instead of a breed the customer can't actually have.
                                value={isUnoffered ? "" : value}
                                options={options}
                                placeholder={petLabel}
                                searchPlaceholder={Strings.SEARCH_BREEDS}
                                onChange={val => {
                                    onChange(val);
                                    // Drops the issue the resolver raised on a blocked Save, so the
                                    // customer isn't left staring at an error they just resolved.
                                    if (val) clearErrors(`policies.${policyIndex}.breedID`);
                                }}
                                wrapperClass="flex-1"
                                error={error}
                                isLoading={catalog.isLoading}
                                variant={floatingVariant}
                                enableFuzzySearch
                                keyByLabel
                                withContainer
                                triggerProps={{
                                    "aria-label": petLabel,
                                    "aria-describedby": error ? `error-modal-pet-breed-${policyIndex}` : undefined
                                }}
                            />
                        </FormField>
                    );
                }}
            />
        </div>
    );
}

export default UserModalBreedFields;
